/**
 * Premium upgrade modal — surfaced from the pilot-selection screen and
 * the in-game pause menu. Renders the two Stripe-backed plans
 * (monthly / yearly) side by side, highlights the savings on the
 * yearly tier, and on plan click opens the hosted Stripe Checkout URL
 * in a new tab.
 *
 * The modal is intentionally self-contained — it owns its own
 * `BillingClient` instance, fetches the live status on open, and emits
 * lifecycle callbacks back to the host screen so the host can refresh
 * its locked-race UI when the player closes after a successful
 * upgrade. We do NOT poll for completion; the server pushes a
 * `PremiumUpdate` over the WebSocket the moment Stripe's webhook lands,
 * which is wired in `Game` and refreshes the modal automatically via
 * `notifyPremium`.
 */
import { t } from '@viral-space/shared';
import {
  COLORS, FONT, GLOW, GLOW_STRONG, el, css,
  injectSharedKeyframes, attachMobileCloseButton, isMobileLayout,
} from './ui-shared';
import { BillingClient, BillingError, type BillingPlan } from '../network/billing-client';
import { isElectronDesktop, isCapacitorIOS } from '../input/mobile';
import {
  isNativeBillingSupported,
  presentProPaywall,
  restoreProPurchases,
  presentCustomerCenter,
  getProStatus,
} from '../network/revenuecat';

/** Where iOS players are sent to upgrade until Apple IAP ships. */
const WEB_UPGRADE_URL = 'https://playzerion.com';

/** DLC App ID baked into the bundle at build time via VITE_STEAM_PREMIUM_DLC_APP_ID. */
const STEAM_DLC_APP_ID: number | null = (() => {
  const raw = import.meta.env.VITE_STEAM_PREMIUM_DLC_APP_ID;
  if (!raw) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
})();

export interface UpgradeModalOptions {
  /** WebSocket URL — used to derive the HTTP base for `/api/billing/*`. */
  serverUrl: string;
  /** Opaque session token (the playerId from `AuthResponse`). */
  billingToken: string | null;
  /** Best-known premium status when the modal is first opened. */
  initialPremium: { active: boolean; currentPeriodEnd: number | null };
  /**
   * Optional source tag for analytics / event reasons. Mostly used by
   * the host screens so the click that produced the modal stays
   * traceable in logs.
   */
  source: 'pilot-select' | 'pause-menu' | 'char-create' | 'shop' | 'warp-gate' | 'planet';
}

interface PlanDescriptor {
  id: BillingPlan;
  title: string;
  price: string;
  cadence: string;
  badge?: string;
  perks: string[];
}

/**
 * Card descriptors are seeded with English copy so the static array
 * keeps building, but `buildPerks(plan.id)` is what's actually
 * rendered — it pulls live i18n strings via the standard `t()` so
 * Spanish + future locales pick up the same perk list automatically.
 * Keep these in sync with `upgrade.perk*` keys in the locale files.
 */
const PLANS: PlanDescriptor[] = [
  {
    id: 'monthly',
    title: 'PREMIUM · MONTHLY',
    price: '$6.99',
    cadence: 'per month',
    perks: [],
  },
  {
    id: 'yearly',
    title: 'PREMIUM · YEARLY',
    price: '$59.99',
    cadence: 'per year',
    badge: 'BEST VALUE · 28% OFF',
    perks: [],
  },
];

/**
 * Build the localized perk list for the given plan card. The shared
 * core of perks is identical between Monthly and Yearly so subscribers
 * see consistent value framing — Yearly just adds the "lock in current
 * price" bullet on top.
 */
function buildPerks(plan: BillingPlan): string[] {
  const core = [
    t('upgrade.perkXpBoost'),
    t('upgrade.perkCreditBoost'),
    t('upgrade.perkUnlimitedWarp'),
    t('upgrade.perkExclusivePlanets'),
    t('upgrade.perkAllRaces'),
    t('upgrade.perkExclusiveItems'),
    t('upgrade.perkWarpDiscount'),
    t('upgrade.perkShopDiscount'),
    t('upgrade.perkMissionSlots'),
    t('upgrade.perkMarketplaceListings'),
    t('upgrade.perkDailyBonus'),
    t('upgrade.perkChatBadge'),
  ];
  if (plan === 'yearly') core.push(t('upgrade.lockInPrice'));
  return core;
}

export class UpgradeModal {
  private container: HTMLElement;
  private root: HTMLElement;
  private backdrop: HTMLElement;
  private panel: HTMLElement;
  private body: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private visible = false;
  private opts: UpgradeModalOptions;
  private billing: BillingClient;
  private busy = false;
  private readonly onKey: (e: KeyboardEvent) => void;
  private onClose: (() => void) | null = null;
  private currentPremium: { active: boolean; currentPeriodEnd: number | null };

  constructor(container: HTMLElement, opts: UpgradeModalOptions) {
    this.container = container;
    this.opts = opts;
    this.currentPremium = { ...opts.initialPremium };
    this.billing = new BillingClient(opts.serverUrl, opts.billingToken);
    injectSharedKeyframes();

    this.root = el('div', {
      position: 'absolute',
      inset: '0',
      zIndex: '4000',
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'auto',
    });
    container.appendChild(this.root);

    this.backdrop = el('div', {
      position: 'absolute',
      inset: '0',
      background: 'radial-gradient(ellipse at center, rgba(0,12,2,0.85), rgba(0,0,0,0.92))',
      backdropFilter: 'blur(6px)',
      opacity: '0',
      transition: 'opacity 0.25s ease',
    });
    this.backdrop.addEventListener('click', () => this.hide());
    this.root.appendChild(this.backdrop);

    this.panel = el('div', {
      position: 'relative',
      zIndex: '1',
      width: 'min(720px, 92vw)',
      maxHeight: '92vh',
      background: 'linear-gradient(180deg, rgba(0,16,2,0.96), rgba(0,8,0,0.96))',
      border: `1px solid ${COLORS.primary}`,
      boxShadow: `0 0 60px rgba(0,255,65,0.18), inset 0 0 30px rgba(0,255,65,0.04)`,
      padding: '0',
      opacity: '0',
      transform: 'scale(0.96) translateY(8px)',
      transition: 'opacity 0.2s ease, transform 0.2s ease',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    });
    this.root.appendChild(this.panel);

    attachMobileCloseButton(this.panel, () => this.hide());

    this.onKey = (e: KeyboardEvent) => {
      if (e.code === 'Escape' && this.visible) {
        e.preventDefault();
        e.stopPropagation();
        this.hide();
      }
    };
    window.addEventListener('keydown', this.onKey, true);
  }

  setBillingToken(token: string | null): void {
    this.opts.billingToken = token;
    this.billing.setToken(token);
  }

  /** Update the premium snapshot (driven by `PremiumUpdate` from server). */
  notifyPremium(p: { active: boolean; currentPeriodEnd: number | null }): void {
    this.currentPremium = { ...p };
    if (this.visible) {
      this.renderBody();
    }
  }

  show(onClose?: () => void): void {
    if (this.visible) return;
    this.visible = true;
    this.onClose = onClose ?? null;
    this.renderShell();
    this.renderBody();
    this.root.style.display = 'flex';
    requestAnimationFrame(() => {
      this.backdrop.style.opacity = '1';
      this.panel.style.opacity = '1';
      this.panel.style.transform = 'scale(1) translateY(0)';
    });
    void this.refreshStatus();
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.backdrop.style.opacity = '0';
    this.panel.style.opacity = '0';
    this.panel.style.transform = 'scale(0.96) translateY(8px)';
    setTimeout(() => {
      if (!this.visible) this.root.style.display = 'none';
    }, 220);
    const cb = this.onClose;
    this.onClose = null;
    if (cb) cb();
  }

  isVisible(): boolean { return this.visible; }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey, true);
    this.root.remove();
  }

  private renderShell(): void {
    this.panel.innerHTML = '';
    const header = el('div', {
      padding: '20px 28px 14px',
      borderBottom: `1px solid ${COLORS.dim}`,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    });
    const title = el('div', {
      fontFamily: FONT,
      fontSize: 'clamp(16px, 2.5vw, 22px)',
      color: COLORS.primary,
      letterSpacing: '6px',
      textTransform: 'uppercase',
      textShadow: GLOW_STRONG,
    }, t('upgrade.title'));
    const subtitle = el('div', {
      fontFamily: FONT,
      fontSize: 'clamp(10px, 1.4vw, 12px)',
      color: COLORS.dimText,
      letterSpacing: '3px',
      textTransform: 'uppercase',
    }, t('upgrade.subtitle'));
    header.append(title, subtitle);
    this.panel.appendChild(header);

    this.body = el('div', {
      padding: '20px 24px 24px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      overflowY: 'auto',
      // Let the body shrink inside the max-height-capped panel so panes
      // that manage their own inner scrolling (the native billing pane
      // pins its CTA row and scrolls only the perk list) can rely on a
      // bounded height instead of growing past the viewport.
      flex: '1',
      minHeight: '0',
    });
    this.panel.appendChild(this.body);

    this.statusEl = el('div', {
      fontFamily: FONT,
      fontSize: '11px',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      color: COLORS.dimText,
      minHeight: '14px',
      textAlign: 'center',
    }, '');
    this.panel.appendChild(this.statusEl);

    const footer = el('div', {
      padding: '12px 28px 16px',
      borderTop: `1px solid ${COLORS.dim}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap',
    });
    const note = el('div', {
      fontFamily: FONT,
      fontSize: '10px',
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      color: COLORS.dimText,
      maxWidth: '60%',
    }, isElectronDesktop()
      ? t('upgrade.poweredBySteam')
      : isNativeBillingSupported()
        ? t('upgrade.poweredByStore')
        : isCapacitorIOS()
          ? t('upgrade.iosUpgradeFooter')
          : t('upgrade.poweredByStripe'));
    const close = el('button', {
      fontFamily: FONT,
      fontSize: '11px',
      padding: '8px 18px',
      background: 'transparent',
      border: `1px solid ${COLORS.dim}`,
      color: COLORS.primary,
      letterSpacing: '3px',
      textTransform: 'uppercase',
      cursor: 'pointer',
    }, t('upgrade.notNow'));
    close.addEventListener('mouseenter', () => {
      close.style.background = 'rgba(0,255,65,0.06)';
      close.style.borderColor = COLORS.primary;
    });
    close.addEventListener('mouseleave', () => {
      close.style.background = 'transparent';
      close.style.borderColor = COLORS.dim;
    });
    close.addEventListener('click', () => this.hide());
    footer.append(note, close);
    this.panel.appendChild(footer);
  }

  private renderBody(): void {
    if (!this.body) return;
    this.body.innerHTML = '';

    if (this.currentPremium.active) {
      this.renderActiveState(this.body);
      return;
    }

    // In the Steam desktop client we MUST NOT route purchases through
    // Stripe — Steam's distribution terms require in-app purchases of
    // Steam content to go through Steam. So the Steam build always uses
    // the Steam-only body (which shows the DLC buy button when configured,
    // or an "unavailable" notice when the DLC id isn't baked into the
    // build). Stripe checkout is reserved for the web + mobile clients.
    if (isElectronDesktop()) {
      this.renderSteamBody(this.body);
      return;
    }

    // Native Capacitor builds (iOS + Android) route purchases through
    // StoreKit / Google Play Billing via the RevenueCat paywall — both
    // stores forbid external checkout for digital goods inside native
    // apps, so the Stripe plan cards must never render here.
    if (isNativeBillingSupported()) {
      this.renderNativeBody(this.body);
      return;
    }

    // iOS build without a RevenueCat key baked in: Apple's guidelines
    // forbid opening an external checkout for digital goods, so we
    // explain that upgrading is only possible from the web
    // (playzerion.com), without a buy button.
    if (isCapacitorIOS()) {
      this.renderIOSBody(this.body);
      return;
    }

    const grid = el('div', {
      display: 'grid',
      gridTemplateColumns: isMobileLayout() ? '1fr' : '1fr 1fr',
      gap: '14px',
    });
    for (const plan of PLANS) {
      grid.appendChild(this.buildPlanCard(plan));
    }
    this.body.appendChild(grid);

    const perksLine = el('div', {
      fontFamily: FONT,
      fontSize: '11px',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      color: COLORS.dimText,
      textAlign: 'center',
      paddingTop: '4px',
    }, t('upgrade.unlockLine'));
    this.body.appendChild(perksLine);
  }

  private renderSteamBody(host: HTMLElement): void {
    const wrap = el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
      padding: '24px 8px',
      textAlign: 'center',
    });

    const perksTitle = el('div', {
      fontFamily: FONT,
      fontSize: '12px',
      color: COLORS.dimText,
      letterSpacing: '2px',
      textTransform: 'uppercase',
    }, t('upgrade.unlockLine'));
    wrap.appendChild(perksTitle);

    const perksList = el('ul', {
      listStyle: 'none',
      margin: '0',
      padding: '0',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      textAlign: 'left',
    });
    for (const p of buildPerks('yearly')) {
      const li = el('li', {
        fontFamily: FONT,
        fontSize: '12px',
        color: COLORS.primary,
        opacity: '0.85',
        letterSpacing: '1px',
      }, '· ' + p);
      perksList.appendChild(li);
    }
    wrap.appendChild(perksList);

    // No DLC App ID baked into this build → premium isn't purchasable yet
    // in the Steam client. Show a notice instead of a buy button, and
    // deliberately do NOT fall back to Stripe (Steam policy).
    if (STEAM_DLC_APP_ID === null) {
      const notice = el('div', {
        marginTop: '8px',
        fontFamily: FONT,
        fontSize: '12px',
        color: COLORS.secondary,
        letterSpacing: '2px',
        textTransform: 'uppercase',
        maxWidth: '440px',
      }, t('upgrade.steamUnavailable'));
      wrap.appendChild(notice);
      host.appendChild(wrap);
      return;
    }

    const buyBtn = el('button', {
      marginTop: '8px',
      fontFamily: FONT,
      fontSize: '14px',
      padding: '12px 32px',
      background: COLORS.primary,
      color: '#000',
      border: `2px solid ${COLORS.primary}`,
      letterSpacing: '4px',
      textTransform: 'uppercase',
      cursor: 'pointer',
      transition: 'background 0.15s, color 0.15s',
      boxShadow: `0 0 20px rgba(0,255,65,0.35)`,
    }, t('upgrade.buyOnSteam'));
    buyBtn.addEventListener('mouseenter', () => {
      buyBtn.style.background = 'transparent';
      buyBtn.style.color = COLORS.primary;
    });
    buyBtn.addEventListener('mouseleave', () => {
      buyBtn.style.background = COLORS.primary;
      buyBtn.style.color = '#000';
    });
    buyBtn.addEventListener('click', () => this.openSteamStore());
    wrap.appendChild(buyBtn);

    // "Already bought it" — re-check ownership without re-logging in.
    const recheckBtn = el('button', {
      fontFamily: FONT,
      fontSize: '11px',
      padding: '8px 20px',
      background: 'transparent',
      border: `1px solid ${COLORS.dim}`,
      color: COLORS.dimText,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      cursor: 'pointer',
    }, t('upgrade.alreadyBought'));
    recheckBtn.addEventListener('click', () => void this.recheckSteamOwnership());
    wrap.appendChild(recheckBtn);

    host.appendChild(wrap);
  }

  /**
   * iOS body: Apple IAP isn't wired up yet and App Store rules forbid
   * sending users to an external checkout for digital goods, so we don't
   * render plan cards or a buy button here. We just list the perks and
   * explain that upgrading is currently only available from the web,
   * with the URL shown as plain selectable text (no clickable link out).
   */
  private renderIOSBody(host: HTMLElement): void {
    const wrap = el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
      padding: '24px 8px',
      textAlign: 'center',
    });

    const perksTitle = el('div', {
      fontFamily: FONT,
      fontSize: '12px',
      color: COLORS.dimText,
      letterSpacing: '2px',
      textTransform: 'uppercase',
    }, t('upgrade.unlockLine'));
    wrap.appendChild(perksTitle);

    const perksList = el('ul', {
      listStyle: 'none',
      margin: '0',
      padding: '0',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      textAlign: 'left',
    });
    for (const p of buildPerks('yearly')) {
      const li = el('li', {
        fontFamily: FONT,
        fontSize: '12px',
        color: COLORS.primary,
        opacity: '0.85',
        letterSpacing: '1px',
      }, '· ' + p);
      perksList.appendChild(li);
    }
    wrap.appendChild(perksList);

    const notice = el('div', {
      marginTop: '8px',
      fontFamily: FONT,
      fontSize: '13px',
      color: COLORS.secondary,
      lineHeight: '1.6',
      letterSpacing: '1px',
      maxWidth: '460px',
    }, t('upgrade.iosUpgradeNotice'));
    wrap.appendChild(notice);

    const urlChip = el('div', {
      fontFamily: FONT,
      fontSize: '15px',
      color: COLORS.primary,
      letterSpacing: '2px',
      padding: '10px 20px',
      border: `1px solid ${COLORS.primary}`,
      background: 'rgba(0,255,65,0.06)',
      textShadow: GLOW,
      userSelect: 'text',
      cursor: 'text',
    }, WEB_UPGRADE_URL.replace(/^https?:\/\//, ''));
    (urlChip.style as CSSStyleDeclaration).webkitUserSelect = 'text';
    wrap.appendChild(urlChip);

    host.appendChild(wrap);
  }

  /**
   * Native (Capacitor iOS / Android) body. Purchases run through the
   * RevenueCat paywall — a remote-configured, store-compliant sheet
   * rendered by the OS on top of the WebView — so this pane only needs
   * the perk list, a primary "VIEW PLANS" CTA, and the App Store
   * review-mandated "RESTORE PURCHASES" affordance. Plan cards and
   * prices are intentionally NOT drawn here: the paywall itself owns
   * the live store pricing (localized currency, intro offers) and can
   * be redesigned from the RevenueCat dashboard without an app update.
   */
  private renderNativeBody(host: HTMLElement): void {
    // The CTA row must never scroll out of view — on a landscape phone
    // (~390px tall) the full perk list is taller than the panel, so the
    // list gets its own scroll region while the buttons stay pinned.
    const wrap = el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px',
      padding: '4px 8px',
      textAlign: 'center',
      flex: '1',
      minHeight: '0',
    });

    const perksTitle = el('div', {
      fontFamily: FONT,
      fontSize: '12px',
      color: COLORS.dimText,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      flexShrink: '0',
    }, t('upgrade.unlockLine'));
    wrap.appendChild(perksTitle);

    const perksList = el('ul', {
      listStyle: 'none',
      margin: '0',
      padding: '0 4px',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      textAlign: 'left',
      overflowY: 'auto',
      minHeight: '0',
    });
    for (const p of buildPerks('yearly')) {
      const li = el('li', {
        fontFamily: FONT,
        fontSize: '12px',
        color: COLORS.primary,
        opacity: '0.85',
        letterSpacing: '1px',
      }, '· ' + p);
      perksList.appendChild(li);
    }
    wrap.appendChild(perksList);

    const plansBtn = el('button', {
      marginTop: '4px',
      fontFamily: FONT,
      fontSize: '14px',
      padding: '12px 32px',
      background: COLORS.primary,
      color: '#000',
      border: `2px solid ${COLORS.primary}`,
      letterSpacing: '4px',
      textTransform: 'uppercase',
      cursor: 'pointer',
      transition: 'background 0.15s, color 0.15s',
      boxShadow: `0 0 20px rgba(0,255,65,0.35)`,
      flexShrink: '0',
    }, t('upgrade.viewPlans'));
    plansBtn.addEventListener('mouseenter', () => {
      plansBtn.style.background = 'transparent';
      plansBtn.style.color = COLORS.primary;
    });
    plansBtn.addEventListener('mouseleave', () => {
      plansBtn.style.background = COLORS.primary;
      plansBtn.style.color = '#000';
    });
    plansBtn.addEventListener('click', () => void this.launchNativePaywall());
    wrap.appendChild(plansBtn);

    const restoreBtn = el('button', {
      fontFamily: FONT,
      fontSize: '11px',
      padding: '8px 20px',
      background: 'transparent',
      border: `1px solid ${COLORS.dim}`,
      color: COLORS.dimText,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      cursor: 'pointer',
      flexShrink: '0',
    }, t('upgrade.restore'));
    restoreBtn.addEventListener('click', () => void this.restoreNativePurchases());
    wrap.appendChild(restoreBtn);

    host.appendChild(wrap);
  }

  /**
   * Present the RevenueCat paywall. Gated on the Pro entitlement, so a
   * player whose subscription synced in while the modal was open just
   * gets flipped straight to the active state instead of a sheet they
   * cannot buy from twice.
   */
  private async launchNativePaywall(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.setStatus(t('upgrade.opening'));
    try {
      const outcome = await presentProPaywall();
      switch (outcome) {
        case 'purchased':
          this.setStatus(t('upgrade.purchaseThanks'), COLORS.primary);
          await this.refreshNativeStatus();
          break;
        case 'restored':
          this.setStatus(t('upgrade.restored'), COLORS.primary);
          await this.refreshNativeStatus();
          break;
        case 'not_presented':
          // Entitlement already active — sync the modal to match.
          await this.refreshNativeStatus();
          break;
        case 'cancelled':
          this.setStatus(t('upgrade.purchaseCancelled'));
          break;
        case 'error':
          this.setStatus(t('upgrade.purchaseError'), COLORS.danger);
          break;
      }
    } finally {
      this.busy = false;
    }
  }

  /** App Store review requires a visible restore path. */
  private async restoreNativePurchases(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.setStatus(t('upgrade.checking'));
    try {
      const status = await restoreProPurchases();
      if (status === null) {
        this.setStatus(t('upgrade.purchaseError'), COLORS.danger);
      } else if (status.active) {
        this.setStatus(t('upgrade.restored'), COLORS.primary);
        this.notifyPremium({ active: true, currentPeriodEnd: status.expirationMillis });
      } else {
        this.setStatus(t('upgrade.restoreNone'), COLORS.secondary);
      }
    } finally {
      this.busy = false;
    }
  }

  /**
   * Pull the freshest entitlement snapshot from the SDK cache and
   * repaint if it differs from what the modal is showing. The global
   * premium state is updated separately by the customer-info listener
   * in `main.ts`; this only keeps the open modal honest.
   */
  private async refreshNativeStatus(): Promise<void> {
    const status = await getProStatus();
    if (status?.active && !this.currentPremium.active) {
      this.notifyPremium({ active: true, currentPeriodEnd: status.expirationMillis });
    }
  }

  private buildPlanCard(plan: PlanDescriptor): HTMLElement {
    const isYear = plan.id === 'yearly';
    const card = el('div', {
      position: 'relative',
      border: `1px solid ${isYear ? COLORS.primary : COLORS.dim}`,
      background: isYear
        ? 'linear-gradient(160deg, rgba(0,40,12,0.7), rgba(0,16,4,0.85))'
        : 'rgba(0,12,4,0.7)',
      padding: '18px 18px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      cursor: 'pointer',
      transition: 'border-color 0.15s, transform 0.15s, box-shadow 0.2s',
      boxShadow: isYear ? `0 0 18px rgba(0,255,65,0.2)` : 'none',
    });

    if (plan.badge) {
      const badge = el('div', {
        position: 'absolute',
        top: '-10px',
        right: '12px',
        background: COLORS.secondary,
        color: '#000',
        padding: '3px 10px',
        fontSize: '10px',
        fontFamily: FONT,
        letterSpacing: '2px',
        textTransform: 'uppercase',
        boxShadow: '0 0 12px rgba(255,176,0,0.5)',
      }, plan.badge);
      card.appendChild(badge);
    }

    const title = el('div', {
      fontFamily: FONT,
      fontSize: '13px',
      color: COLORS.primary,
      letterSpacing: '4px',
      textTransform: 'uppercase',
      textShadow: GLOW,
    }, plan.title);
    card.appendChild(title);

    const priceRow = el('div', {
      display: 'flex',
      alignItems: 'baseline',
      gap: '8px',
    });
    const price = el('div', {
      fontFamily: FONT,
      fontSize: '32px',
      color: COLORS.primary,
      textShadow: GLOW_STRONG,
      letterSpacing: '2px',
    }, plan.price);
    const cadence = el('div', {
      fontFamily: FONT,
      fontSize: '11px',
      color: COLORS.dimText,
      textTransform: 'uppercase',
      letterSpacing: '2px',
    }, plan.cadence);
    priceRow.append(price, cadence);
    card.appendChild(priceRow);

    const perks = el('ul', {
      listStyle: 'none',
      margin: '4px 0 0',
      padding: '0',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    });
    for (const p of buildPerks(plan.id)) {
      const li = el('li', {
        fontFamily: FONT,
        fontSize: '12px',
        color: COLORS.primary,
        opacity: '0.85',
        letterSpacing: '1px',
      }, '· ' + p);
      perks.appendChild(li);
    }
    card.appendChild(perks);

    const cta = el('button', {
      marginTop: '10px',
      fontFamily: FONT,
      fontSize: '13px',
      padding: '10px 0',
      background: isYear ? COLORS.primary : 'transparent',
      color: isYear ? '#000' : COLORS.primary,
      border: `2px solid ${COLORS.primary}`,
      letterSpacing: '4px',
      textTransform: 'uppercase',
      cursor: 'pointer',
      transition: 'background 0.15s, color 0.15s',
    }, t('upgrade.cta'));
    cta.addEventListener('mouseenter', () => {
      cta.style.background = COLORS.primary;
      cta.style.color = '#000';
    });
    cta.addEventListener('mouseleave', () => {
      cta.style.background = isYear ? COLORS.primary : 'transparent';
      cta.style.color = isYear ? '#000' : COLORS.primary;
    });
    const launch = (e: Event) => {
      e.stopPropagation();
      void this.startCheckout(plan.id);
    };
    cta.addEventListener('click', launch);
    card.addEventListener('click', launch);

    card.addEventListener('mouseenter', () => {
      card.style.borderColor = COLORS.primary;
      card.style.transform = 'translateY(-2px)';
      card.style.boxShadow = `0 0 24px rgba(0,255,65,0.28)`;
    });
    card.addEventListener('mouseleave', () => {
      card.style.borderColor = isYear ? COLORS.primary : COLORS.dim;
      card.style.transform = 'translateY(0)';
      card.style.boxShadow = isYear ? `0 0 18px rgba(0,255,65,0.2)` : 'none';
    });

    return card;
  }

  private renderActiveState(host: HTMLElement): void {
    const wrap = el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px',
      padding: '20px 8px',
      textAlign: 'center',
    });
    const badge = el('div', {
      fontFamily: FONT,
      fontSize: '13px',
      color: '#000',
      background: COLORS.secondary,
      padding: '6px 14px',
      letterSpacing: '4px',
      textTransform: 'uppercase',
      boxShadow: '0 0 16px rgba(255,176,0,0.55)',
    }, t('upgrade.activeBadge'));
    const head = el('div', {
      fontFamily: FONT,
      fontSize: '20px',
      color: COLORS.primary,
      letterSpacing: '4px',
      textTransform: 'uppercase',
      textShadow: GLOW_STRONG,
    }, t('upgrade.activeTitle'));
    const sub = el('div', {
      fontFamily: FONT,
      fontSize: '12px',
      color: COLORS.dimText,
      letterSpacing: '2px',
      maxWidth: '420px',
    }, isNativeBillingSupported() ? t('upgrade.activeBodyNative') : t('upgrade.activeBody'));
    wrap.append(badge, head, sub);

    if (this.currentPremium.currentPeriodEnd) {
      const ends = new Date(this.currentPremium.currentPeriodEnd);
      const renew = el('div', {
        fontFamily: FONT,
        fontSize: '11px',
        color: COLORS.primary,
        opacity: '0.7',
        letterSpacing: '2px',
        textTransform: 'uppercase',
      }, `${t('upgrade.nextRenewal')} ${ends.toLocaleDateString()}`);
      wrap.appendChild(renew);
    }

    const portalBtn = el('button', {
      marginTop: '8px',
      fontFamily: FONT,
      fontSize: '12px',
      padding: '10px 24px',
      background: 'transparent',
      border: `1px solid ${COLORS.primary}`,
      color: COLORS.primary,
      letterSpacing: '3px',
      textTransform: 'uppercase',
      cursor: 'pointer',
    }, t('upgrade.managePortal'));
    portalBtn.addEventListener('mouseenter', () => {
      portalBtn.style.background = COLORS.primary;
      portalBtn.style.color = '#000';
    });
    portalBtn.addEventListener('mouseleave', () => {
      portalBtn.style.background = 'transparent';
      portalBtn.style.color = COLORS.primary;
    });
    portalBtn.addEventListener('click', () => void this.openPortal());
    wrap.appendChild(portalBtn);

    host.appendChild(wrap);
  }

  private setStatus(text: string, color: string = COLORS.dimText): void {
    if (!this.statusEl) return;
    this.statusEl.textContent = text;
    this.statusEl.style.color = color;
  }

  private async refreshStatus(): Promise<void> {
    // Native builds: the store entitlement can be newer than the
    // server's Stripe/Steam view (e.g. the player subscribed on the
    // paywall seconds ago), so consult the RevenueCat cache first.
    if (isNativeBillingSupported()) {
      await this.refreshNativeStatus();
    }
    if (!this.opts.billingToken) return;
    try {
      const snap = await this.billing.status();
      if (!snap.billingEnabled && !snap.active) {
        this.setStatus(t('upgrade.disabledHint'), COLORS.secondary);
      }
      if (snap.active !== this.currentPremium.active) {
        this.notifyPremium({
          active: snap.active,
          currentPeriodEnd: snap.currentPeriodEnd,
        });
      }
    } catch (err) {
      if (err instanceof BillingError && err.status === 401) {
        this.setStatus(t('upgrade.sessionExpired'), COLORS.danger);
      }
    }
  }

  private async startCheckout(plan: BillingPlan): Promise<void> {
    if (this.busy) return;
    if (!this.opts.billingToken) {
      this.setStatus(t('upgrade.sessionExpired'), COLORS.danger);
      return;
    }
    this.busy = true;
    this.setStatus(t('upgrade.opening'));
    try {
      const resp = await this.billing.checkout(plan);
      // Open in a new tab so the live game session stays connected. The
      // player can come back to the game with the WebSocket still open
      // and the server pushes a `PremiumUpdate` once the webhook fires.
      this.openExternal(resp.url);
      this.setStatus(t('upgrade.checkoutOpened'), COLORS.primary);
    } catch (err) {
      console.warn('[upgrade] checkout failed', err);
      const code = err instanceof BillingError ? err.message : 'unknown';
      if (code === 'billing_disabled') {
        this.setStatus(t('upgrade.disabledHint'), COLORS.secondary);
      } else if (code === 'price_not_configured') {
        this.setStatus(t('upgrade.priceMissing'), COLORS.danger);
      } else {
        this.setStatus(t('upgrade.checkoutError'), COLORS.danger);
      }
    } finally {
      this.busy = false;
    }
  }

  private async openPortal(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.setStatus(t('upgrade.opening'));
    try {
      // Native builds manage store subscriptions through the RevenueCat
      // Customer Center (cancel / plan change / refund requests happen
      // in-app); the Stripe customer portal only knows about web
      // subscriptions and cannot manage an App Store / Play purchase.
      if (isNativeBillingSupported()) {
        const ok = await presentCustomerCenter();
        this.setStatus(ok ? '' : t('upgrade.portalError'), ok ? COLORS.dimText : COLORS.danger);
        return;
      }
      const resp = await this.billing.portal();
      this.openExternal(resp.url);
    } catch (err) {
      console.warn('[upgrade] portal failed', err);
      this.setStatus(t('upgrade.portalError'), COLORS.danger);
    } finally {
      this.busy = false;
    }
  }

  /** Open the Steam DLC store overlay so the player can purchase Premium. */
  private openSteamStore(): void {
    const steam = (window as Window & typeof globalThis).zerionDesktop?.steam;
    if (steam && STEAM_DLC_APP_ID !== null) {
      steam.activateOverlayStore(STEAM_DLC_APP_ID);
      this.setStatus(t('upgrade.steamOverlayOpened'), COLORS.primary);
    }
  }

  /**
   * Re-check DLC ownership via the server billing endpoint. Called when the
   * player clicks "I already bought it" after purchasing in the Steam overlay.
   * The server re-reads the Steamworks Web API with the current session
   * ticket so no re-login is required.
   */
  private async recheckSteamOwnership(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.setStatus(t('upgrade.checking'));
    try {
      const snap = await this.billing.status();
      if (snap.active) {
        this.notifyPremium({ active: true, currentPeriodEnd: snap.currentPeriodEnd });
        this.setStatus(t('upgrade.steamPremiumActive'), COLORS.primary);
      } else {
        this.setStatus(t('upgrade.steamNotOwned'), COLORS.secondary);
      }
    } catch {
      this.setStatus(t('upgrade.checkoutError'), COLORS.danger);
    } finally {
      this.busy = false;
    }
  }

  /**
   * Open an external URL in a new tab without ever navigating the host
   * window. Implemented via a synthetic anchor click rather than
   * `window.open(url, '_blank', 'noopener,noreferrer')` because the
   * `noopener`/`noreferrer` window features cause `window.open` to
   * return `null` even on success, which made the previous "popup
   * blocked, fall back to `location.href = url`" branch fire on every
   * successful open and yank the game tab to Stripe alongside the new
   * tab. A real `<a target="_blank" rel="noopener noreferrer">` click
   * gets the same security treatment without that footgun, and any
   * browser-level popup block surfaces the standard popup-blocked UI
   * instead of corrupting the game session.
   */
  private openExternal(url: string): void {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.style.display = 'none';
    document.body.appendChild(a);
    try {
      a.click();
    } finally {
      a.remove();
    }
  }
}

void GLOW;
void css;
